/**
 * Dependency-free funnel analytics for the lead form. Emits `lead-form-*` events
 * to Segment (`window.analytics`), relays them to a parent page via postMessage
 * (for iframe embeds that fire their own tags), and to an optional host hook.
 *
 * The property builder and brand-delta are pure (unit-tested); `createTracker`
 * touches globals so it's excluded from coverage.
 */

export type LeadFormEvent =
  | 'lead-form-viewed'
  | 'lead-form-started'
  | 'lead-form-seller-type-selected'
  | 'lead-form-field-completed'
  | 'lead-form-field-errored'
  | 'lead-form-brand-added'
  | 'lead-form-brand-removed'
  | 'lead-form-us-answered'
  | 'lead-form-lead-channel-selected'
  | 'lead-form-submit-attempted'
  | 'lead-form-submit-blocked'
  | 'lead-form-submitted'
  | 'lead-form-submit-failed'
  | 'lead-form-outcome'
  | 'lead-form-meeting-viewed'
  | 'lead-form-meeting-booked'
  | 'lead-form-abandoned'

/**
 * Funnel events for the payout-calculator popup. Named to mirror the lead
 * form's shape so both widgets can be reported on with one set of conventions.
 */
export type PayoutCalcEvent =
  | 'payout-calc-viewed'
  | 'payout-calc-started'
  | 'payout-calc-email-submitted'
  | 'payout-calc-field-completed'
  | 'payout-calc-brand-added'
  | 'payout-calc-brand-removed'
  | 'payout-calc-calculated'
  | 'payout-calc-submitted'
  | 'payout-calc-submit-failed'
  | 'payout-calc-cta-clicked'
  | 'payout-calc-dismissed'

/** Any funnel event this package emits. */
export type WidgetEvent = LeadFormEvent | PayoutCalcEvent

export type EventProps = Record<string, unknown>

export interface ITrackerContext {
  formInstanceId: string
  sourceUrl: string
  referrer: string
  embedded: boolean
  /** GA4 client id, for the Segment → GA4 Cloud destination. See
   * `resolveGaClientId`. Never empty: the destination's Client ID field is
   * required, and an event with no client id is dropped. */
  gaClientId: string
  /** Google Ads click id, for matching the server-side conversion event to
   * the ad click without needing a website tag. See `resolveGclid`. Empty
   * for non-ad traffic — there is no fallback, unlike `gaClientId`. */
  gclid: string
}

/**
 * Pure: extract a GA4 `client_id` from a raw `_ga` cookie value.
 *
 * The cookie looks like `GA1.1.1234567890.1234567890`, but GA4 wants only the
 * trailing `<random>.<timestamp>` pair. Accepts an already-stripped value too,
 * so the host page can pass the cookie through verbatim without knowing the
 * format. Returns '' if there's nothing usable.
 */
export const normalizeGaClientId = (raw?: string | null): string => {
  if (!raw) {
    return ''
  }
  const parts = raw.split('.').filter(Boolean)
  if (parts.length < 2) {
    return ''
  }
  return parts.slice(-2).join('.')
}

/**
 * Pure: extract a bare Google Ads click id from a raw value.
 *
 * A `gclid` passed straight from the ad-click landing URL is already bare.
 * Google's own `_gcl_aw` cookie instead looks like `GCL.<timestamp>.<gclid>`,
 * so this also unwraps that shape. Returns '' if there's nothing usable.
 */
export const normalizeGclid = (raw?: string | null): string => {
  if (!raw) {
    return ''
  }
  const parts = raw.split('.').filter(Boolean)
  return parts.length >= 3 ? parts[parts.length - 1] : raw
}

/**
 * The five standard UTM query params, and the `ITracker`/`IUtmParams` key each
 * maps to. Shared with `attribution.ts` (the host-page loader) so the param
 * names it reads off the landing URL and forwards into the iframe can't drift
 * from the names read back out here.
 */
export const UTM_PARAMS = {
  source: 'utm_source',
  medium: 'utm_medium',
  campaign: 'utm_campaign',
  term: 'utm_term',
  content: 'utm_content',
} as const

export interface IUtmParams {
  source: string
  medium: string
  campaign: string
  term: string
  content: string
}

/**
 * Pure: read the five UTM params off a `URLSearchParams`, defaulting each to
 * ''. Unlike `gclid`/`gaClientId` there is no cookie fallback — UTM params
 * only ever come from the landing URL itself.
 */
export const pickUtmParams = (search: URLSearchParams): IUtmParams => {
  return {
    source: search.get(UTM_PARAMS.source) ?? '',
    medium: search.get(UTM_PARAMS.medium) ?? '',
    campaign: search.get(UTM_PARAMS.campaign) ?? '',
    term: search.get(UTM_PARAMS.term) ?? '',
    content: search.get(UTM_PARAMS.content) ?? '',
  }
}

/**
 * Pure: merge the per-attempt context into an event's properties (snake_case).
 *
 * `analytics_client_id` deliberately avoids a `ga_` prefix. GA4 reserves `ga_`,
 * `google_` and `firebase_` for parameter names and **silently drops any event
 * carrying one** — while Measurement Protocol still answers 204, so Segment
 * reports it delivered. That cost a week of funnel data (2026-08-08 → 08-10),
 * because the GA4 destination forwards this whole object as Event Parameters.
 * Segment's Client ID mapping reads `properties.analytics_client_id`; renaming
 * this key means updating that mapping in the same window, or Client ID goes
 * empty and GA4 drops the events for a different reason.
 */
export const buildEventProps = (
  ctx: ITrackerContext,
  extra?: EventProps
): EventProps => {
  return {
    /* eslint-disable @typescript-eslint/naming-convention */
    form_instance_id: ctx.formInstanceId,
    analytics_client_id: ctx.gaClientId,
    source_url: ctx.sourceUrl,
    referrer: ctx.referrer,
    embedded: ctx.embedded,
    /* eslint-enable @typescript-eslint/naming-convention */
    ...extra,
  }
}

export interface IBrandDelta {
  action: 'added' | 'removed'
  brand: string
}

/**
 * Pure: given the previously- and newly-selected brand lists, return the single
 * brand that changed (the autocomplete only adds/removes one at a time).
 */
export const brandDelta = (
  previous: string[],
  next: string[]
): IBrandDelta | undefined => {
  if (next.length > previous.length) {
    const added = next.find((b) => !previous.includes(b))
    return added === undefined ? undefined : { action: 'added', brand: added }
  }
  if (next.length < previous.length) {
    const removed = previous.find((b) => !next.includes(b))
    return removed === undefined
      ? undefined
      : { action: 'removed', brand: removed }
  }
  return undefined
}

export type Track = (event: WidgetEvent, extra?: EventProps) => void

export interface ITracker {
  track: Track
  /** Per-attempt id, also sent to the backend so the server-side conversion
   * event can be joined to this client funnel. */
  instanceId: string
  /** Resolved GA4 client id, also sent to the backend so the server-side
   * conversion event lands on the same GA4 user as this client funnel. */
  gaClientId: string
  /** Resolved Google Ads click id, also sent to the backend so the
   * server-side conversion event can match to the originating ad click. */
  gclid: string
  /** Resolved UTM params, submitted to HubSpot as Company fields alongside
   * the rest of the lead form's values. See `resolveUtmParams`. */
  utm: IUtmParams
}

export interface ITrackerOptions {
  onEvent?: (event: WidgetEvent, props: EventProps) => void
  /**
   * Message/CustomEvent name used to relay events out of the widget. Defaults
   * to the lead form's channel; the payout popup passes its own so a host page
   * can tell the two widgets apart.
   */
  channel?: string
}

interface ISegmentAnalytics {
  track: (event: string, props?: EventProps) => void
}
/* eslint-disable @typescript-eslint/naming-convention */
declare global {
  interface Window {
    analytics?: ISegmentAnalytics
  }
}
/* eslint-enable @typescript-eslint/naming-convention */

// Analytics is best-effort — a sink error must never break the form.
const safeInvoke = (fn: () => void): void => {
  try {
    fn()
  } catch {
    // swallow: tracking failures are non-fatal
  }
}

/**
 * Resolve the GA4 client id to attribute this form session to.
 *
 * The form runs on its own domain inside an iframe, so it can't see the host
 * page's `_ga` cookie — without help, GA4 would treat every lead as a brand-new
 * user with no campaign attribution. The host page therefore passes its own
 * `_ga` value in as `?ga_cid=…` (see the embed snippet in the README).
 *
 * Fallbacks, in order: the `ga_cid` param → our own `_ga` cookie (set only if
 * something else on this domain runs gtag) → the form instance id, so the value
 * is never empty. The last case makes a direct (non-embedded) visit its own GA4
 * user, which is correct enough: there's no host session to join.
 */
/* istanbul ignore next -- touches globals; exercised via the demo/staging, not jsdom */
const resolveGaClientId = (formInstanceId: string): string => {
  const fromParam = new URLSearchParams(window.location.search).get('ga_cid')
  const fromCookie = document.cookie
    .split('; ')
    .find((c) => c.startsWith('_ga='))
    ?.slice('_ga='.length)
  return (
    normalizeGaClientId(fromParam) ||
    normalizeGaClientId(fromCookie) ||
    formInstanceId
  )
}

/**
 * Resolve the Google Ads click id to attribute this form session to.
 *
 * Same cross-domain problem as `resolveGaClientId`: the form can't see the
 * host page's landing URL or `_gcl_aw` cookie, so the host passes its own
 * value in as `?gclid=…` (see the embed snippet in the README). Unlike
 * `gaClientId`, there is no fallback — most sessions genuinely have no
 * click id, and a made-up value would falsely attribute them to an ad.
 */
/* istanbul ignore next -- touches globals; exercised via the demo/staging, not jsdom */
const resolveGclid = (): string => {
  const fromParam = new URLSearchParams(window.location.search).get('gclid')
  const fromCookie = document.cookie
    .split('; ')
    .find((c) => c.startsWith('_gcl_aw='))
    ?.slice('_gcl_aw='.length)
  return normalizeGclid(fromParam) || normalizeGclid(fromCookie)
}

/**
 * Resolve the UTM params to attribute this form session to.
 *
 * Same cross-domain problem as `resolveGaClientId`/`resolveGclid`: the form
 * can't see the host page's landing URL, so the host forwards its own UTM
 * params onto the iframe `src` (see `withAttribution` in `attribution.ts`),
 * and this reads them back off the iframe's own URL.
 */
/* istanbul ignore next -- touches globals; exercised via the demo/staging, not jsdom */
const resolveUtmParams = (): IUtmParams =>
  pickUtmParams(new URLSearchParams(window.location.search))

/* istanbul ignore next -- touches globals; exercised via the demo/staging, not jsdom */
export const createTracker = (opts: ITrackerOptions = {}): ITracker => {
  const formInstanceId = `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`
  const ctx: ITrackerContext = {
    formInstanceId,
    gaClientId: resolveGaClientId(formInstanceId),
    gclid: resolveGclid(),
    sourceUrl: window.location.href,
    referrer: document.referrer,
    embedded: window.parent !== window,
  }
  const channel = opts.channel ?? 'mr-lead-form-event'
  const track: Track = (event, extra) => {
    const props = buildEventProps(ctx, extra)
    safeInvoke(() => window.analytics?.track(event, props))
    if (ctx.embedded) {
      safeInvoke(() =>
        window.parent.postMessage(
          { type: channel, event, properties: props },
          '*'
        )
      )
    }
    // Same-document event so the host page can react (e.g. hide its intro on
    // submit). Fires whether standalone or embedded, unlike the postMessage.
    safeInvoke(() =>
      window.dispatchEvent(
        new CustomEvent(channel, {
          detail: { event, properties: props },
        })
      )
    )
    safeInvoke(() => opts.onEvent?.(event, props))
  }
  return {
    track,
    instanceId: ctx.formInstanceId,
    gaClientId: ctx.gaClientId,
    gclid: ctx.gclid,
    utm: resolveUtmParams(),
  }
}
